import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch, UseFormSetValue } from 'react-hook-form';
import { getImageURL, replaceJSX, toBase64 } from '../../../helpers';
import { CustomTypography } from '../../custom/CustomTypography';
import ColorPicker from '../../settings/editWidgetPanel/ColorPicker';
import styles from './ProviderWidget.module.css';
import Switch from '@mui/material/Switch';
import { IProvider, I1CParams, IIdmParams, ILdapParams } from 'src/redux/services/provider';

interface TProviderWidgetProps {
  providerName?: string;
}

export const ProviderWidget: FC<TProviderWidgetProps> = ({ providerName }) => {
  const {
    control,
    setValue,
    formState: { errors },
    register,
  } = useFormContext<IProvider<I1CParams | IIdmParams | ILdapParams>>();

  const buttonColor = useWatch({ control, name: 'params.provider_colors.button_color' });
  const fontColor = useWatch({ control, name: 'params.provider_colors.font_color' });
  if (!buttonColor) {
    setValue('params.provider_colors.button_color', '#4C6AD4');
  }
  if (!fontColor) {
    setValue('params.provider_colors.font_color', '#fff');
  }

  const showAvatar = useWatch({ control, name: 'params.show_provider_avatar' });
  const title = useWatch({ control, name: 'params.provider_title' });
  const uploadedAvatar = useWatch({ control, name: 'avatar' });

  const [avatar, setAvatar] = useState(uploadedAvatar);

  useEffect(() => {
    const update = async () => {
      if (uploadedAvatar instanceof Blob) {
        const src = await toBase64(uploadedAvatar);
        setAvatar(src);
      }
    };
    update();
  }, [uploadedAvatar]);

  return (
    <div className={styles['ldap-color']}>
      <div>
        <CustomTypography className={clsx('text-14')}>
          Показывать логотип провайдера в виджете
        </CustomTypography>
        <Controller
          name="params.show_provider_avatar"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <Switch
              data-id="show-provider-avatar-switch"
              classes={{ root: styles['switch-root'] }}
              disableRipple
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />
      </div>
      <CustomTypography className={clsx('text-14', styles['color-title'])}>
        Заголовок способа входа
      </CustomTypography>
      <TextField
        {...register('params.provider_title')}
        className="custom"
        fullWidth
        variant="standard"
        style={{ marginBottom: 32 }}
      />
      <div className={styles['flex-container']}>
        <div>
          <div className={styles['set-color']}>
            <CustomTypography className={clsx('text-14', styles['color-title'])}>
              Цвет кнопок
            </CustomTypography>
            <ColorPicker componentName="params.provider_colors" colorName="button_color" />
            {errors.params?.provider_colors?.button_color && (
              <CustomTypography color="error" className={clsx('text-14', styles['color-error'])}>
                {errors.params?.provider_colors?.button_color.message}
              </CustomTypography>
            )}
          </div>
          <div className={styles['set-color']}>
            <CustomTypography className={clsx('text-14', styles['color-title'])}>
              Цвет шрифта на кнопках
            </CustomTypography>
            <ColorPicker componentName="params.provider_colors" colorName="font_color" />
            {errors.params?.provider_colors?.font_color && (
              <CustomTypography color="error" className={clsx('text-14', styles['color-error'])}>
                {errors.params?.provider_colors?.font_color.message}
              </CustomTypography>
            )}
          </div>
        </div>
        <div className={styles['ldap-wrapper']}>
          <CustomTypography className={clsx('text-14', styles.preview)}>Превью:</CustomTypography>
          <div className={styles.ldap}>
            <CustomTypography className={styles['ldap-title']}>
              Авторизуйтесь пользователем Trusted
            </CustomTypography>
            <div className={styles.divider} />
            <div className={styles['title-container']}>
              {showAvatar && avatar && (
                <img
                  src={
                    avatar instanceof File
                      ? URL.createObjectURL(avatar)
                      : avatar?.startsWith('data:')
                      ? avatar
                      : getImageURL(avatar)
                  }
                  width="40px"
                  height="40px"
                  style={{ margin: '0 10px', objectFit: 'cover' }}
                />
              )}
              <CustomTypography className={clsx(styles['ldap-subtitle'])}>
                {replaceJSX(
                  title || '',
                  'PROVIDER_TITLE',
                  <span key={'key'} style={{ color: buttonColor }}>
                    {providerName}
                  </span>,
                )}
              </CustomTypography>
            </div>
            <TextField
              label="Логин"
              variant="standard"
              InputProps={{
                classes: {
                  underline: styles['input-underline'],
                  focused: styles['ldap-input-label-focused'],
                },
              }}
              InputLabelProps={{
                className: styles['ldap-input-label'],
                classes: {
                  focused: styles['ldap-input-label-focused'],
                  asterisk: styles['disable-asterisk'],
                },
              }}
              fullWidth
              className={styles['text-field']}
            />
            <TextField
              label="Пароль"
              variant="standard"
              InputProps={{
                classes: {
                  underline: styles['input-underline'],
                  focused: styles['ldap-input-label-focused'],
                },
              }}
              InputLabelProps={{
                className: styles['ldap-input-label'],
                classes: {
                  focused: styles['ldap-input-label-focused'],
                  asterisk: styles['disable-asterisk'],
                },
              }}
              fullWidth
              className={styles['text-field']}
              type="password"
            />
            <div className={styles['ldap-buttons']}>
              <Button style={{ color: buttonColor }} variant="custom2">
                СМЕНИТЬ ПАРОЛЬ
              </Button>
              <Button style={{ background: buttonColor, color: fontColor }} variant="custom">
                ВОЙТИ
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
